if (!customElements.get('cart-notification')) {
  customElements.define(
    'cart-notification',
    class CartNotification extends HTMLElement {
      constructor() {
        super();

        this.notification =
          document.getElementById('cart-notification');

        this.header = document.querySelector('sticky-header');

        this.onBodyClick =
          this.handleBodyClick.bind(this);

        this.onQuantityChange = debounce(
          this.handleQuantityChange.bind(this),
          300
        );

        this.notification?.addEventListener(
          'keyup',
          (event) => {
            if (event.code === 'Escape') {
              this.close();
            }
          }
        );

        this.querySelectorAll(
          '.cart-notification__close, .phi-continue-shopping'
        ).forEach((closeButton) => {
          closeButton.addEventListener(
            'click',
            this.close.bind(this)
          );
        });

        this.addEventListener(
          'change',
          this.onQuantityChange
        );
      }

      open() {
        if (!this.notification) return;

        this.notification.classList.add(
          'animate',
          'active'
        );

        this.notification.addEventListener(
          'transitionend',
          () => {
            this.notification.focus();
            trapFocus(this.notification);
          },
          { once: true }
        );

        document.body.addEventListener(
          'click',
          this.onBodyClick
        );
      }

      close() {
        if (!this.notification) return;

        this.notification.classList.remove('active');

        document.body.removeEventListener(
          'click',
          this.onBodyClick
        );

        removeTrapFocus(this.activeElement);
      }

      renderContents(parsedState) {
        this.cartItemKey = parsedState.key;

        this.getSectionsToRender().forEach((section) => {
          const target =
            document.getElementById(section.id);

          const sectionHTML =
            parsedState.sections?.[section.id];

          if (!target || !sectionHTML) return;

          const innerHTML = this.getSectionInnerHTML(
            sectionHTML,
            section.selector
          );

          if (innerHTML !== null) {
            target.innerHTML = innerHTML;
          }
        });

        if (this.header) {
          this.header.reveal();
        }

        this.open();
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-notification-product',
            selector: this.cartItemKey
              ? `[id="cart-notification-product-${this.cartItemKey}"]`
              : null,
          },
          {
            id: 'cart-notification-button',
          },
          {
            id: 'cart-icon-bubble',
          },
        ];
      }

      getSectionInnerHTML(
        html,
        selector = '.shopify-section'
      ) {
        const parsedHTML = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const selectedElement = selector
          ? parsedHTML.querySelector(selector)
          : parsedHTML.querySelector('.shopify-section');

        return selectedElement
          ? selectedElement.innerHTML
          : null;
      }

      handleBodyClick(event) {
        const target = event.target;

        if (
          target !== this.notification &&
          !target.closest('cart-notification')
        ) {
          const disclosure = target.closest(
            'details-disclosure, header-menu'
          );

          this.activeElement = disclosure
            ? disclosure.querySelector('summary')
            : null;

          this.close();
        }
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      async handleQuantityChange(event) {
        const quantityInput = event.target.closest(
          '[data-cart-notification-quantity]'
        );

        if (!quantityInput) return;

        const itemKey =
          quantityInput.dataset.itemKey;

        const minimum =
          parseInt(
            quantityInput.min ||
              quantityInput.dataset.min ||
              '1',
            10
          ) || 1;

        const maximum = parseInt(
          quantityInput.max ||
            quantityInput.dataset.max ||
            '',
          10
        );

        let quantity =
          parseInt(quantityInput.value, 10) || minimum;

        quantity = Math.max(minimum, quantity);

        if (Number.isFinite(maximum)) {
          quantity = Math.min(maximum, quantity);
        }

        quantityInput.value = quantity;

        if (!itemKey) return;

        await this.updateQuantity(
          itemKey,
          quantity,
          quantityInput
        );
      }

      async updateQuantity(
        itemKey,
        quantity,
        quantityInput
      ) {
        const itemContainer = quantityInput.closest(
          '[data-cart-item-key]'
        );

        const statusElement =
          itemContainer?.querySelector(
            '[data-cart-notification-status]'
          );

        const errorElement =
          itemContainer?.querySelector(
            '[data-cart-notification-error]'
          );

        const quantityContainer =
          quantityInput.closest('quantity-input');

        const quantityButtons =
          quantityContainer?.querySelectorAll('button');

        quantityInput.disabled = true;

        quantityButtons?.forEach((button) => {
          button.disabled = true;
        });

        if (statusElement) {
          statusElement.textContent = 'Updating cart…';
        }

        if (errorElement) {
          errorElement.hidden = true;
          errorElement.textContent = '';
        }

        try {
          const requestBody = JSON.stringify({
            id: itemKey,
            quantity,
            sections: this
              .getSectionsToRender()
              .map((section) => section.id),
            sections_url: window.location.pathname,
          });

          const response = await fetch(
            routes.cart_change_url,
            {
              ...fetchConfig(),
              body: requestBody,
            }
          );

          const parsedState = await response.json();

          if (!response.ok || parsedState.errors) {
            throw new Error(
              parsedState.description ||
                parsedState.message ||
                parsedState.errors ||
                'Unable to update quantity.'
            );
          }

          this.getSectionsToRender().forEach((section) => {
            const target =
              document.getElementById(section.id);

            const sectionHTML =
              parsedState.sections?.[section.id];

            if (!target || !sectionHTML) return;

            const selector =
              section.id ===
                'cart-notification-product'
                ? `[id="cart-notification-product-${itemKey}"]`
                : section.selector;

            const innerHTML =
              this.getSectionInnerHTML(
                sectionHTML,
                selector
              );

            if (innerHTML !== null) {
              target.innerHTML = innerHTML;
            }
          });

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-notification',
            cartData: parsedState,
          });
        } catch (error) {
          quantityInput.value =
            quantityInput.dataset.currentQuantity || 1;

          quantityInput.disabled = false;

          quantityButtons?.forEach((button) => {
            button.disabled = false;
          });

          if (statusElement) {
            statusElement.textContent = '';
          }

          if (errorElement) {
            errorElement.textContent =
              error.message ||
              'Quantity could not be updated. Please try again.';

            errorElement.hidden = false;
          }

          console.error(error);
        }
      }
    }
  );
}